/* eslint-disable no-spaced-func */
/* eslint-disable func-call-spacing */
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DataField } from "@iventis/domain-model/model/dataField";
import { Asset } from "@iventis/domain-model/model/asset";
import { MapObject } from "@iventis/domain-model/model/mapObject";
import { DataFieldType } from "@iventis/domain-model/model/dataFieldType";
import { borderRadius, inputMarginBottom } from "@iventis/styles/src/atomic-rules";
import { InteractiveElement } from "@iventis/styles/src/components";
import { Body2, Subtitle } from "@iventis/styles/src/components/texts";
import { Content } from "@iventis/translations/content/typed-content";
import { HeightWidthField, LoadingComponent } from "@iventis/components";
import { LengthField } from "@iventis/components/src/length-field";
import React, { FunctionComponent, useEffect, useMemo, useRef, useState } from "react";
import { styled } from "@iventis/styles";
import debounce from "lodash.debounce";
import { DataFieldValueTypes } from "@iventis/types";
import { Theme } from "@emotion/react";
import { MapLayer } from "@iventis/domain-model/model/mapLayer";
import { orderUniqueDataFields, extractRelevantDataFieldRelationships, getFilteredDataFieldTypesForLayer, isAdmin } from "@iventis/utilities";
import { userLocalStorageItems, userLocalStorageManager } from "@iventis/local-storage/src/user-local-storage";
import { distanceRhumb, getCoordinateList, setDistanceBetweenCoordinates, resizeRectangleInAxis } from "@iventis/map-engine/src/utilities/geojson-helpers";
import { CSSTransition } from "react-transition-group";
import { UnitOfMeasurement } from "@iventis/domain-model/model/unitOfMeasurement";
import { DataFieldConfigContext, DataFieldServicesContext, DataFieldsChips, IDataFieldServices, useDataFieldServices } from "@iventis/datafield-editor";
import { Authorisation } from "@iventis/domain-model/model/authorisation";
import { SystemDataFieldName } from "@iventis/domain-model/model/systemDataFieldName";
import {
    getMapObjectAreaSystemDataField,
    getMapObjectCoordinatesSystemDataField,
    getMapObjectIconRotationSystemDataField,
    getMapObjectLengthSystemDataField,
    getMapObjectNameSystemDatafield,
    getMapObjectQuantityOfModelsInAreaSystemDataField,
    getMapObjectQuantityOfModelsOnLineSystemDataField,
    getMapObjectRotationZOfModelsInAreaSystemDataField,
    getMapObjectRouteTimeSystemDataField,
    getModelAltitudeSystemDataField,
} from "@iventis/datafields";
import { ObjectDataField } from "./object-data-field";

type SavingStatus = "idle" | "saving" | "pending";

export const ObjectDetailsComponent: FunctionComponent<{
    mapObjectsSelected: MapObject[];
    mapLayersSelected: MapLayer[];
    saveChanges: (mapObjects: MapObject[]) => Promise<void>;
    updateGeom: (mapObject: MapObject) => void;
    loading: boolean;
    translate?: (text: string) => string;
    onEditAttributeClicked: (e: React.MouseEvent, dataField: DataField) => void;
    onManageAttributesClicked: () => void;
    isObjectDetailsMinimised: boolean;
    setObjectDetailsMinimised: (isMinimised: boolean) => void;
    setTransitionCompleted: (transitionCompleted: boolean) => void;
    transitionCompleted: boolean;
    userId?: string;
    canUserEdit: boolean;
    unitOfMeasurement: UnitOfMeasurement;
    getAsset: (assetId: string) => Promise<Asset>;
    projectDataFields: DataField[];
    layerPermissions: { [layerId: string]: Authorisation };
}> = ({
    mapObjectsSelected,
    mapLayersSelected,
    saveChanges,
    loading,
    translate,
    onEditAttributeClicked,
    updateGeom,
    isObjectDetailsMinimised,
    setObjectDetailsMinimised,
    setTransitionCompleted,
    transitionCompleted,
    userId,
    canUserEdit,
    unitOfMeasurement,
    getAsset,
    projectDataFields,
    layerPermissions,
}) => {
    const [dataFields, setDataFields] = useState<DataField[]>([]);

    const systemDataFields = useMemo(
        () => ({
            [SystemDataFieldName.MapObjectName]: getMapObjectNameSystemDatafield(projectDataFields),
            [SystemDataFieldName.MapObjectArea]: getMapObjectAreaSystemDataField(projectDataFields),
            [SystemDataFieldName.MapObjectLength]: getMapObjectLengthSystemDataField(projectDataFields),
            [SystemDataFieldName.Coordinates]: getMapObjectCoordinatesSystemDataField(projectDataFields),
            [SystemDataFieldName.RouteTime]: getMapObjectRouteTimeSystemDataField(projectDataFields),
            [SystemDataFieldName.QuantityOfModelsOnLine]: getMapObjectQuantityOfModelsOnLineSystemDataField(projectDataFields),
            [SystemDataFieldName.QuantityOfModelsInArea]: getMapObjectQuantityOfModelsInAreaSystemDataField(projectDataFields),
            [SystemDataFieldName.RotationZOfModelsInArea]: getMapObjectRotationZOfModelsInAreaSystemDataField(projectDataFields),
            [SystemDataFieldName.IconRotation]: getMapObjectIconRotationSystemDataField(projectDataFields),
            [SystemDataFieldName.ModelAltitude]: getModelAltitudeSystemDataField(projectDataFields),
        }),
        [projectDataFields]
    );
    // used to keep track of transitioning child
    const nodeRef = React.useRef(null);

    // Saving state
    const [savingState, setSavingState] = useState<SavingStatus>("idle");

    const [objects, setObjects] = useState<MapObject[]>(mapObjectsSelected);
    useEffect(() => {
        if (!mapObjectsSelected.every((mapObject) => objects.map((object) => object.geoJsonFeature).includes(mapObject.geoJsonFeature))) {
            setObjects(mapObjectsSelected);
        }
    }, [mapObjectsSelected]);

    const componentIsMounted = useRef(null);
    useEffect(() => {
        componentIsMounted.current = true;
        return () => {
            componentIsMounted.current = false;
        };
    }, []);

    const stringifiedSelectedObjects = useMemo(() => JSON.stringify(objects.map(({ id }) => id)), [objects]);
    const stringifiedSelectedLayers = useMemo(() => JSON.stringify(mapLayersSelected.map(({ id }) => id)), [mapLayersSelected]);

    // We stringify the array so that the use effect does a value comparison on the string to check if the ids have changed, we cannot reference the object as
    // technically it will be a new / different object every rerender therefore the use effect will run even if we haven't changed which object we have selected.
    // If we change map object
    useEffect(() => {
        // Clean + flush save
        setSavingState("idle");
        saveDebounceRef.current.flush();
        setObjects(mapObjectsSelected);
    }, [stringifiedSelectedObjects]);

    // Save map objects
    // This function will is called within debounce which is also a useRef
    // Be careful accessing the state here
    const saveObjectsChanges = async (updatedObjects: MapObject[]) => {
        if (componentIsMounted.current) {
            setSavingState("saving");
        }
        await saveChanges(updatedObjects);
        if (componentIsMounted.current) {
            setSavingState("idle");
        }
    };

    // Save the map object, debounced
    // Debounce needs to be a ref, otherwise the reference to the debounced function is lost
    // And you cannot debounce it again or flush
    const saveDebounceRef = React.useRef(debounce(saveObjectsChanges, 1000));

    // Cancel any previous debounce and add the new objects
    const setNewObjects = (objects: MapObject[], debounce: Boolean = true) => {
        setObjects(objects);
        if (debounce) {
            saveDebounceRef.current.cancel();
            saveDebounceRef.current(objects);
            setSavingState("pending");
        } else {
            saveObjectsChanges(objects);
        }
    };

    const handleDataFieldChange = (dataField: DataField, value: DataFieldValueTypes) => {
        // Replace data field values in all objects
        const newObjects = objects.map(
            (object) =>
                ({
                    ...object,
                    dataFieldValues: {
                        ...object.dataFieldValues,
                        [dataField.id]: value,
                    },
                } as MapObject)
        );
        // Hyperlink attribute handles it's own debounce as it is used inside the component state
        if (dataField.type === DataFieldType.List || dataField.type === DataFieldType.Tickbox || dataField.type === DataFieldType.Hyperlink) {
            setNewObjects(newObjects, false);
        } else {
            setNewObjects(newObjects);
        }
    };

    // If we change layer
    useEffect(() => {
        // Clean + flush save
        setSavingState("idle");
        saveDebounceRef.current.flush();
    }, [stringifiedSelectedLayers]);

    // If the layer array changes
    useEffect(() => {
        const newDataFields: DataField[] = [];
        mapLayersSelected.forEach((lyr) => {
            lyr.dataFields?.forEach((df) => {
                if (mapLayersSelected.every((l) => l.dataFields?.some((d) => d.id === df.id))) {
                    newDataFields.push(df);
                }
            });
        });
        setDataFields(newDataFields);
    }, [mapLayersSelected]);

    const orderedDataFields = useMemo(() => orderUniqueDataFields(dataFields), [dataFields]);

    const onTransitionEnd = () => {
        setTransitionCompleted(!transitionCompleted);
    };

    const { dataFieldsService, dataFieldListItemsService } = useDataFieldServices();

    // Intercept data field post to add order
    const dataFieldsServiceInterceptor: IDataFieldServices = useMemo(
        () => ({
            dataFieldListItemsService,
            dataFieldsService: {
                ...dataFieldsService,
                postDataField: async (dataField, resourceId) => {
                    const orderedDataField = { ...dataField, order: orderedDataFields.length + 1 };
                    setDataFields([...dataFields, orderedDataField]);
                    await dataFieldsService.postDataField(orderedDataField, resourceId);
                },
            },
        }),
        [dataFieldsService]
    );

    const lockedLayersSelected = useMemo(() => mapLayersSelected.some((l) => l.locked), [mapLayersSelected]);
    return (
        <>
            {loading && (
                <StyledLoadingContainer $isObjectDetailsMinimised={isObjectDetailsMinimised}>
                    <LoadingComponent data-testid="object-details-loading" />
                </StyledLoadingContainer>
            )}
            {!loading && (
                <>
                    <div className="header">
                        <div className="title">
                            <FontAwesomeIcon icon="circle-info" />
                            <Subtitle>
                                <span>{translate(Content.map5.object_details.object_details)}</span>
                                <span> </span>
                                {!canUserEdit && <span>{`(${translate(Content.permissions.authLevels.read.title)})`}</span>}
                            </Subtitle>
                            {/* Lock icon */}
                            {lockedLayersSelected && <FontAwesomeIcon data-testid="map-object-details-lock" icon={["fas", "lock"]} />}
                            {isObjectDetailsMinimised && (
                                <StyledMapObjectSelectionWrapper>
                                    (
                                    <StyledMapObjectSelection>
                                        {objects.length > 1 ? (
                                            <span>{translate(Content.map2.object.multiple_objects)}</span>
                                        ) : (
                                            <span>{objects[0]?.dataFieldValues[systemDataFields[SystemDataFieldName.MapObjectName]?.id]}</span>
                                        )}
                                    </StyledMapObjectSelection>
                                    )
                                </StyledMapObjectSelectionWrapper>
                            )}
                        </div>
                        <InteractiveElement
                            className="minimise"
                            data-testid="toggle-object-details-button"
                            onClick={() => {
                                userLocalStorageManager.set(userLocalStorageItems.objectDetailsPanel, userId, !isObjectDetailsMinimised);
                                setObjectDetailsMinimised(!isObjectDetailsMinimised);
                            }}
                        >
                            <span className="hidden">{translate(Content.map5.object_details.minimise)}</span>
                            <FontAwesomeIcon icon={isObjectDetailsMinimised ? "chevrons-up" : "chevrons-down"} />
                        </InteractiveElement>
                    </div>
                    <CSSTransition nodeRef={nodeRef} in={!isObjectDetailsMinimised} timeout={300} className="minimise-objectDetails-transition" unmountOnExit>
                        <div ref={nodeRef} onTransitionEnd={onTransitionEnd}>
                            <div className="objectDetails-content">
                                <ObjectDataAndDimensionFields
                                    key={stringifiedSelectedObjects}
                                    orderedDataFields={orderedDataFields}
                                    objects={objects}
                                    onValueChange={(dataField, value) => handleDataFieldChange(dataField, value)}
                                    onDimensionChange={(object) => !lockedLayersSelected && updateGeom(object)}
                                    disabled={!canUserEdit || lockedLayersSelected}
                                    unitOfMeasurement={unitOfMeasurement}
                                    systemDataFields={systemDataFields}
                                    onEditAttributeClicked={onEditAttributeClicked}
                                    canUserEdit={canUserEdit}
                                    getAsset={getAsset}
                                    layer={mapLayersSelected[0]}
                                    layerPermissions={layerPermissions}
                                    mapObjectName={objects[0]?.dataFieldValues[systemDataFields[SystemDataFieldName.MapObjectName]?.id]}
                                />
                                {mapLayersSelected.length === 1 && mapLayersSelected.every((mapLayer) => isAdmin(layerPermissions[mapLayer.id])) && (
                                    <div style={{ display: "flex", justifyContent: "right" }}>
                                        {/* ToDo: On add data field, trigger onEdit clicked */}
                                        <DataFieldServicesContext.Provider value={dataFieldsServiceInterceptor}>
                                            <DataFieldConfigContext.Provider
                                                value={{ resourceId: mapLayersSelected[0].id, allowSetDefaultValue: true, validateListItem: () => true }}
                                            >
                                                <StyledDatafieldChips
                                                    dataFields={orderedDataFields}
                                                    allowEdit
                                                    getProjectDataFields={() => Promise.resolve(projectDataFields)}
                                                    showExistingChips={false}
                                                    filteredDataFieldTypes={getFilteredDataFieldTypesForLayer(mapLayersSelected[0].styleType)}
                                                />
                                            </DataFieldConfigContext.Provider>
                                        </DataFieldServicesContext.Provider>
                                    </div>
                                )}
                            </div>

                            <div className="bottom-interactions">
                                <StyledSuccess $savingState={savingState} className="bottom-save-display">
                                    <Body2 style={{ alignSelf: "center" }}>
                                        {savingState === "saving" ? (
                                            <span>{translate(Content.common.saving)}</span>
                                        ) : savingState === "pending" ? (
                                            <span>{translate(Content.common.pending_save)}</span>
                                        ) : (
                                            <span>{translate(Content.common.saved)}</span>
                                        )}
                                    </Body2>
                                    {savingState === "saving" && <FontAwesomeIcon icon="circle-notch" spin />}
                                    {savingState === "pending" && <FontAwesomeIcon icon="circle-exclamation" />}
                                    {savingState === "idle" && <FontAwesomeIcon icon="circle-check" data-testid="object-details-saved" />}
                                </StyledSuccess>
                            </div>
                        </div>
                    </CSSTransition>
                </>
            )}
        </>
    );
};

const ObjectDataAndDimensionFields: FunctionComponent<{
    orderedDataFields: DataField[];
    objects: MapObject[];
    onValueChange: (dataField: DataField, value: DataFieldValueTypes) => void;
    onDimensionChange: (newObject: MapObject) => void;
    disabled: boolean;
    systemDataFields: Record<SystemDataFieldName, DataField>;
    unitOfMeasurement: UnitOfMeasurement;
    onEditAttributeClicked: (e: React.MouseEvent, dataField: DataField) => void;
    canUserEdit: boolean;
    getAsset: (assetId: string) => Promise<Asset>;
    layer: MapLayer;
    mapObjectName: string;
    layerPermissions: { [layerId: string]: Authorisation };
}> = ({
    orderedDataFields,
    objects,
    onValueChange,
    onDimensionChange,
    disabled,
    systemDataFields,
    unitOfMeasurement,
    onEditAttributeClicked,
    canUserEdit,
    getAsset,
    layer,
    mapObjectName,
}) => {
    const handleDimensionChange = (newValue: number, axis: "x" | "y") => {
        if (objects.length === 1) {
            const oldObject = objects[0];

            const newGeom = resizeRectangleInAxis(oldObject.geoJsonFeature.geometry, newValue, axis);

            const newObject: MapObject = {
                ...oldObject,
                geoJsonFeature: {
                    ...oldObject.geoJsonFeature,
                    geometry: newGeom,
                },
            };

            onDimensionChange(newObject);
        }
    };
    const getValuesForDataField = (dataFieldId: string) => objects.map((object) => object.dataFieldValues[dataFieldId]);
    const dataFieldsComponents = orderedDataFields.map((dataField) => {
        const relationshipValues: Record<string, unknown[]> = extractRelevantDataFieldRelationships(orderedDataFields, dataField, getValuesForDataField);

        // We should disable if one of the relevant relations have multiple values or if it contains a null value
        const shouldDisable = Object.values(relationshipValues).some((valueList) => valueList.some((val) => val == null || valueList[0] !== val));

        if (dataField.systemDataFieldName === SystemDataFieldName.RouteTime && !(objects?.length === 1 && objects[0].modeOfTransport != null)) {
            return null;
        }

        let fieldType: "attribute" | "editable-line-length" = "attribute";

        const isEditableLineLength =
            canUserEdit &&
            dataField.systemDataFieldName === SystemDataFieldName.MapObjectLength &&
            objects.length === 1 &&
            objects[0]?.geoJsonFeature?.geometry.type === "LineString" &&
            objects[0]?.geoJsonFeature?.geometry.coordinates.length === 2;

        if (isEditableLineLength) {
            fieldType = "editable-line-length";
        }

        const disabledDataFieldIds = [
            ...(objects.length > 1 ? [systemDataFields[SystemDataFieldName.MapObjectName]?.id] : []),
            systemDataFields[SystemDataFieldName.MapObjectArea]?.id,
            systemDataFields[SystemDataFieldName.MapObjectLength]?.id,
            systemDataFields[SystemDataFieldName.Coordinates]?.id,
            systemDataFields[SystemDataFieldName.RouteTime]?.id,
            systemDataFields[SystemDataFieldName.QuantityOfModelsOnLine]?.id,
        ];

        return (
            <StyledDataFieldWrapper key={dataField.id} data-testid="attribute-input-container" $numberOfLabels={dataField.type === DataFieldType.TimeRange ? 2 : 1}>
                <StyledDataField $showAttributeEdit={false}>
                    {fieldType === "editable-line-length" && (
                        <LengthAttributeField objects={objects} disabled={!canUserEdit} unitOfMeasurement={unitOfMeasurement} valueChanged={onDimensionChange} />
                    )}
                    {fieldType === "attribute" && (
                        <ObjectDataField
                            dataField={dataField}
                            systemDataFields={systemDataFields}
                            values={getValuesForDataField(dataField.id)}
                            valueChanged={(value) => onValueChange(dataField, value)}
                            disabled={disabled || shouldDisable || disabledDataFieldIds.includes(dataField.id)}
                            unitOfMeasurement={unitOfMeasurement}
                            relationshipValues={relationshipValues}
                            layer={layer}
                            mapObjectName={mapObjectName}
                            getAsset={getAsset}
                            onEditAttributeSettingsClicked={(e) => onEditAttributeClicked(e, dataField)}
                            canUserEdit={canUserEdit}
                        />
                    )}
                </StyledDataField>
            </StyledDataFieldWrapper>
        );
    });

    // Insert HeightWidth in fields if fixed shape
    if (objects[0]?.fixedShape && objects.length === 1) {
        const coords = getCoordinateList(objects[0].geoJsonFeature.geometry);

        const validFixedShaope = coords?.[0] && coords?.[1] && coords?.[2];

        const height = validFixedShaope && distanceRhumb([...coords[0]], [...coords[1]]);
        const width = validFixedShaope && distanceRhumb([...coords[1]], [...coords[2]]);
        const heightWidthComp = (
            <HeightWidthField
                key="height-width-input"
                values={[height, width]}
                valueChanged={(value, axis) => handleDimensionChange(value, axis)}
                disabled={objects.length > 1 || disabled}
                unitOfMeasurement={unitOfMeasurement}
            />
        );

        // Place it next to the measurement system df i.e. area, length
        const indexToInsertDimensionInputs =
            orderedDataFields.findIndex(
                (df) =>
                    df?.systemDataFieldName === SystemDataFieldName.MapObjectArea ||
                    df?.systemDataFieldName === SystemDataFieldName.MapObjectLength ||
                    df?.systemDataFieldName === SystemDataFieldName.Coordinates
            ) + 1;
        return <>{[...dataFieldsComponents.slice(0, indexToInsertDimensionInputs), heightWidthComp, ...dataFieldsComponents.slice(indexToInsertDimensionInputs)]}</>;
    }

    return <>{dataFieldsComponents}</>;
};

/** Length attribute editor */
const LengthAttributeField = ({
    objects,
    valueChanged,
    disabled,
    unitOfMeasurement,
}: {
    objects: MapObject[];
    valueChanged: (value: MapObject) => void;
    disabled: boolean;
    unitOfMeasurement: UnitOfMeasurement;
}) => {
    const coords = getCoordinateList(objects[0].geoJsonFeature.geometry);

    const validSingleLine = coords?.[0] && coords?.[1];

    const length = validSingleLine && distanceRhumb([...coords[0]], [...coords[1]]);

    const onChange = (newValue: number) => {
        if (newValue <= 0) {
            return;
        }
        const oldObject = objects[0];
        const newCoords = setDistanceBetweenCoordinates(oldObject.geoJsonFeature.geometry.coordinates[0], oldObject.geoJsonFeature.geometry.coordinates[1], newValue);

        const newObject: MapObject = {
            ...oldObject,
            geoJsonFeature: {
                ...oldObject.geoJsonFeature,
                geometry: { ...oldObject.geoJsonFeature.geometry, coordinates: newCoords },
            },
        };

        valueChanged(newObject);
    };

    return (
        <LengthField
            disabled={disabled}
            value={length}
            valueChanged={onChange}
            unitOfMeasurement={unitOfMeasurement}
            inputTestId="attribute-value-Length"
            fieldTestId="length-data-field"
            className="input-margin-bottom"
        />
    );
};

const StyledDatafieldChips = styled(DataFieldsChips)`
    .add-attribute-button {
        margin-top: ${inputMarginBottom};
        color: ${({ theme }: { theme: Theme }) => theme.typographyColors.blank};
        border-color: ${({ theme }: { theme: Theme }) => theme.typographyColors.blank};
    }
`;

const StyledMapObjectSelectionWrapper = styled.div`
    display: flex;
    min-width: 0;
    width: 100%;
`;

const StyledMapObjectSelection = styled.p`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const StyledLoadingContainer = styled.div<{ $isObjectDetailsMinimised: boolean }>`
    height: ${(props) => (props.$isObjectDetailsMinimised ? "56px" : "406px")};
`;

const StyledDataFieldWrapper = styled.div<{ $numberOfLabels: number }>`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;

    .edit-attribute-button-container {
        width: 10%;
        display: flex;
        justify-content: flex-end;
        align-items: center;
    }

    .edit-attribute-button {
        // Margin must account for each label row
        margin-top: ${(props) => props.$numberOfLabels * 17.5}px;
        svg {
            margin-top: 0px;
        }
    }
`;

const StyledDataField = styled.div<{ $showAttributeEdit: boolean }>`
    width: ${(props) => (props.$showAttributeEdit ? "90%;" : "100%")};
    .MuiInputBase-root {
        background-color: ${(props: { theme: Theme }) => props.theme.secondaryColors.blank};
        border-radius: ${borderRadius.input};
    }
    .time-range-selector-label {
        color: ${(props: { theme: Theme }) => props.theme.secondaryColors.blank};
    }
`;

const StyledSuccess = styled.div<{ $savingState: SavingStatus }>`
    ${(props) => (props.$savingState === "idle" ? `color: ${props.theme.secondaryColors.accept}` : "")};
    svg {
        align-self: center;
        width: 20px;
    }
`;
